// The "send us this e-mail" step, shared by /ow-cancel and /ow-plan.
//
// Both pages reach a point where the request has to leave the browser and there
// is no endpoint to take it. Rather than a button that quietly does nothing, the
// user is told exactly what to send and given a button that opens their mail app
// with the address, subject and body already filled in - plus a copy button for
// the case where the machine has no mail client wired up, which is common enough
// on a gaming PC to design for rather than ignore.
//
// Styling is passed in by the host page (`block`), so this renders inside either
// page's card without dragging a second stylesheet along.

import { useCallback, useState } from 'react';
import type { AccountRequestEmail } from './account-request-email';
import { copyToClipboard } from '../utils/clipboard';

interface Props {
  /** BEM block of the host page, e.g. 'ow-cancel' or 'ow-plan'. */
  block: string;
  request: AccountRequestEmail;
  /** What the button says - the two pages ask for different things. */
  action: string;
}

export default function AccountRequestEmailStep({ block, request, action }: Props) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    // copyToClipboard wants a toast; these pages have no toast host, so the
    // button reports its own result instead.
    copyToClipboard(
      {
        success: () => setCopied(true),
        error: () => setCopied(false),
      },
      request.plainText,
    );
  }, [request.plainText]);

  return (
    <div className={`${block}__mail`}>
      <dl className={`${block}__mail-rows`}>
        <div>
          <dt>Send to</dt>
          <dd>{request.to}</dd>
        </div>
        <div>
          <dt>Subject</dt>
          <dd>{request.subject}</dd>
        </div>
      </dl>

      <div className={`${block}__mail-actions`}>
        <a className={`${block}__mail-open`} href={request.mailto}>
          {action}
        </a>
        <button className={`${block}__mail-copy`} type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy the details'}
        </button>
      </div>
    </div>
  );
}
